import { computed, get, set } from '@ember/object';
import lodashSum from 'lodash.sum';
import moment from 'moment-timezone';
import { monthsBetweenDates } from 'cio/utils/journey-metrics';
import { TimeRange } from 'cio/utils/time';

export enum Grouping {
  Hourly = 'hourly',
  Daily = 'daily',
  Weekly = 'weekly',
  Monthly = 'monthly',
}

export type GroupingItem = {
  value: Grouping;
  label: string;
  shouldShow(days: number): boolean;
};

export const GROUPING_DAILY_ITEM: GroupingItem = {
  value: Grouping.Daily,
  label: 'Day',
  shouldShow() {
    return true;
  },
};
export const GROUPING_WEEKLY_ITEM: GroupingItem = {
  value: Grouping.Weekly,
  label: 'Week',
  shouldShow(days: number) {
    return days >= 14;
  },
};
export const GROUPING_MONTHLY_ITEM: GroupingItem = {
  value: Grouping.Monthly,
  label: 'Month',
  shouldShow(days: number) {
    return days >= 60;
  },
};

export const GROUPING_SELECT_ITEMS = [GROUPING_DAILY_ITEM, GROUPING_WEEKLY_ITEM, GROUPING_MONTHLY_ITEM];

export function computedGroupingItems(timeRangePropertyName: string) {
  return computed(`${timeRangePropertyName}.{start,end}`, function () {
    let timeRange = this[timeRangePropertyName];

    if (!timeRange.start || !timeRange.end) {
      return GROUPING_SELECT_ITEMS;
    }

    let daysDiff = timeRange.end.diff(timeRange.start, 'days');

    return GROUPING_SELECT_ITEMS.filter((group) => group.shouldShow(daysDiff));
  });
}

export function selectLargestGroupingAvailableIfNecessary(
  context: any,
  groupingPropertyName: string,
  groupingItems: GroupingItem[],
) {
  let grouping = get(context, groupingPropertyName);
  // If the previously selected grouping is no longer available, change it to
  // the largest possible
  if (!groupingItems.some((group) => group.value === grouping)) {
    let lastAvailableGroupingItem = groupingItems[groupingItems.length - 1];
    set(context, groupingPropertyName, lastAvailableGroupingItem.value);
  }
}

export function groupData(data: number[], ranges: number[]) {
  let dataCopy = [...data];

  let groups = [];
  for (let i = 0; i < ranges.length; i++) {
    let length = ranges[i];
    groups.push(lodashSum(dataCopy.splice(0, length)));
  }
  return groups;
}

export function groupTimestamps(timeRange: TimeRange, grouping: Grouping, timezone = 'Etc/UTC') {
  let range = {
    start: moment.tz(`${timeRange.start?.format('YYYY-MM-DD')} 00:00:00`, timezone),
    end: moment.tz(`${timeRange.end?.format('YYYY-MM-DD')} 00:00:00`, timezone),
  };

  let timestamps: number[] = [];
  if (grouping === Grouping.Daily) {
    let start = moment(range.start).unix();
    let steps = range.end.diff(range.start, 'days');
    timestamps = [start * 1000, ...[...new Array(steps)].map((_, i) => (start + (i + 1) * 24 * 60 * 60) * 1000)];
  } else if (grouping === Grouping.Weekly) {
    let start = range.start.unix();
    let steps = range.end.diff(range.start, 'weeks');
    timestamps = [start * 1000, ...[...new Array(steps)].map((_, i) => (start + (i + 1) * 24 * 60 * 60 * 7) * 1000)];
  } else if (grouping === Grouping.Monthly) {
    timestamps = monthsBetweenDates(range.start, range.end).map(
      (dateString) => moment.tz(dateString, timezone).startOf('month').unix() * 1000,
    );
  }

  return timestamps;
}
